import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Controller,
  ResolverOptions,
  ResolverResult,
  useForm,
} from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { parse, isValid, isAfter, startOfDay } from 'date-fns';
import { z } from 'zod';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { MIN_DISCHARGE_DATE, ResponsiveFormRow } from '@ldt/features/orders';
import {
  BillingInformationFormSchema,
  BillingInformationFormSection,
} from '@ldt/features/orders/components/billing_information_form_section';
import { FileUpload } from '@ldt/features/orders/components/file_upload';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { LdtFormActions } from '@ldt/features/orders/components/ldt_form_actions';
import { LdtOrderFormHeader } from '@ldt/features/orders/components/order_form_header';
import { OrderingProviderFormSection } from '@ldt/features/orders/components/ordering_provider_form_section';
import {
  PatientInformationFormSchema,
  PatientInformationFormSection,
} from '@ldt/features/orders/components/patient_information_form_section';
import {
  createLdtPostRpTStageAndSurgicalMarginFieldsSchema,
  LdtPostRpTStageAndSurgicalMarginFields,
} from '@ldt/features/orders/components/post_rp_t_stage_and_surgical_margin_form_fields';
import { PreventLeaveDuringUploadDialog } from '@ldt/features/orders/components/prevent_leave_during_upload_dialog';
import {
  computeLabProcedureDateWarning,
  getProcedureDateBeforeDateOfBirthError,
  SpecimenInformationFormSchema,
  SpecimenInformationFormSection,
} from '@ldt/features/orders/components/specimen_information_form_section';
import { useLdtFileUploads } from '@ldt/features/orders/hooks/use_ldt_file_uploads';
import { useOrderingProviderFormSection } from '@ldt/features/orders/hooks/use_ordering_provider_form_section';
import { OrderingProviderForm } from '@ldt/features/orders/ordering_provider_form';
import { canUseClinicalNotProvided } from '@ldt/features/orders/utils/clinical_not_provided';
import { allowSoftClinicalSubmit } from '@ldt/features/orders/utils/order_missing_info';
import { OrderRepository } from 'src/api/order_repository';
import { DecimalInput } from 'src/components/decimal_input';
import { SectionHeader } from 'src/components/section_header';
import {
  GleasonScoreFields,
  OPTIONAL_GLEASON_SCORE_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  processFetcherResponse,
  safeEmptyToUndefined,
  safeParseInt,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  useEffectiveOrganization,
  useIsLdtSalesOrCustomerSuccessUser,
  useIsOrderingPhysician,
} from 'src/hooks/use_auth';
import { useDebounce } from 'src/hooks/use_debounce';
import { useNewOrderSignal } from 'src/hooks/use_new_order_signal';
import {
  Ethnicity,
  LdtOrderDetailFieldsFragment,
  OrderStatus,
  OrderingPathway,
  PatientStatusAtBiopsy,
  PrimaryDiagnosisCode,
  SpecimenType,
  TStage,
  LdtUploadedFileFieldsFragment,
  UploadedFileCategory,
  UploadedFileStatus,
  SurgicalMargin,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { WorkflowType } from 'src/types/workflows';
import { notifyError, notifySuccess } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '100%',
  MIN_AGE: 18,
  MAX_AGE: 100,
  MAX_PSA: 30,
  PSA_MIN: 0,
} as const;

// Type for form data - inferred from the schema created with default values
export type LdtProstatePostRPFormData = z.infer<
  ReturnType<typeof createLdtProstatePostRPFormSchema>
>;

export const createLdtProstatePostRPFormSchema = (
  isCSOrSalesUser: boolean,
  orderingPathway: OrderingPathway,
  isEditingQCPendingOrder: boolean,
  allowTStageNotProvided = false,
  workflowType: WorkflowType = WorkflowType.CLINIC,
) => {
  const softClinicalSubmit = allowSoftClinicalSubmit(
    isCSOrSalesUser,
    isEditingQCPendingOrder,
    workflowType,
  );

  return z.object({
    // Ordering Provider — optional for CS/Sales soft submit (Clinic + DU)
    orderingProviderId: softClinicalSubmit
      ? z.string()
      : z.string().min(1, 'Ordering Provider is required'),

    // Patient Information
    ...PatientInformationFormSchema.shape,

    // Clinical Information
    primaryGleasonScore: isCSOrSalesUser
      ? z.enum(['3', '4', '5'], {
          error: 'Primary Gleason Score is required',
        })
      : OPTIONAL_GLEASON_SCORE_SCHEMA,
    secondaryGleasonScore: isCSOrSalesUser
      ? z.enum(['3', '4', '5'], {
          error: 'Secondary Gleason Score is required',
        })
      : OPTIONAL_GLEASON_SCORE_SCHEMA,
    baselinePsa: z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'PSA level is required' };
          }
          return null;
        },
      })
      .min(
        FORM_CONSTANTS.PSA_MIN,
        `PSA must be at least ${FORM_CONSTANTS.PSA_MIN} ng/mL`,
      )
      .max(
        FORM_CONSTANTS.MAX_PSA,
        `PSA must not exceed ${FORM_CONSTANTS.MAX_PSA} ng/mL`,
      )
      .nullable()
      .superRefine((val, ctx) => {
        if (!softClinicalSubmit && val === null) {
          ctx.addIssue({ code: 'custom', message: 'PSA level is required' });
        }
      }),

    ...createLdtPostRpTStageAndSurgicalMarginFieldsSchema(
      allowTStageNotProvided,
      allowTStageNotProvided && !isEditingQCPendingOrder,
    ).shape,

    // Specimen Information (required for CS/Sales QC Pending edit)
    ...SpecimenInformationFormSchema(
      SpecimenType.ProstatePostRp,
      isCSOrSalesUser && isEditingQCPendingOrder,
    ).shape,

    // Billing Information
    ...BillingInformationFormSchema(WorkflowType.CLINIC).shape,

    // File Uploads
    // TRF is required for CS/Sales users unless pathway is StickerTrfNonReflexive
    // TRF is never required for non-CS/Sales users
    testRequisitionForm:
      isCSOrSalesUser &&
      orderingPathway !== OrderingPathway.StickerTrfNonReflexive
        ? z
            .instanceof(File, {
              message: 'Test Requisition Form is required',
            })
            .nullable()
            .refine((file) => file !== null, {
              message: 'Test Requisition Form is required',
            })
        : z.instanceof(File).nullable().optional(),
    // Pathology Report is required when CS/Sales user is editing a QC Pending order
    pathologyReport: isEditingQCPendingOrder
      ? z
          .instanceof(File, { message: 'Pathology Report is required' })
          .nullable()
          .refine((file) => file !== null, {
            message: 'Pathology Report is required',
          })
      : z.instanceof(File).nullable().optional(),
    // Face Sheet is required when CS/Sales user is editing a QC Pending order
    faceSheet: isEditingQCPendingOrder
      ? z
          .instanceof(File, {
            message: 'Demographic / Face Sheet is required',
          })
          .nullable()
          .refine((file) => file !== null, {
            message: 'Demographic / Face Sheet is required',
          })
      : z.instanceof(File).nullable().optional(),
  });
};

// Custom resolver - only handles blocking errors for CS users
export const createProstatePostRPCustomResolver =
  (
    isCSOrSalesUser: boolean,
    orderingPathway: OrderingPathway,
    isEditingQCPendingOrder: boolean,
    warningsRef?: {
      current: Partial<Record<keyof LdtProstatePostRPFormData, string>>;
    },
    allowTStageNotProvided = false,
    workflowType: WorkflowType = WorkflowType.CLINIC,
  ) =>
  async (
    data: LdtProstatePostRPFormData,
    context: unknown,
    options: ResolverOptions<LdtProstatePostRPFormData>,
  ) => {
    const schema = createLdtProstatePostRPFormSchema(
      isCSOrSalesUser,
      orderingPathway,
      isEditingQCPendingOrder,
      allowTStageNotProvided,
      workflowType,
    );
    const zodResult = await zodResolver(schema)(data, context, options);

    // Manual cross-field validation (blocking errors only)
    const customErrors = { ...zodResult.errors };

    // Compute warnings (non-blocking) - these will be returned from the resolver
    const warnings: Partial<Record<keyof LdtProstatePostRPFormData, string>> =
      {};

    // PSA warning
    const psaWarning = computePsaWarning(data.baselinePsa ?? undefined);
    if (psaWarning) {
      warnings.baselinePsa = psaWarning;
    }

    if (isCSOrSalesUser && data.dateOfProcedure) {
      const dateWarning = computeLabProcedureDateWarning(
        data.dateOfProcedure,
        SpecimenType.ProstatePostRp,
      );
      if (dateWarning) {
        warnings.dateOfProcedure = dateWarning;
      }
    }

    const procedureDateBeforeDobError = getProcedureDateBeforeDateOfBirthError(
      data,
      SpecimenType.ProstatePostRp,
    );
    if (procedureDateBeforeDobError) {
      customErrors.dateOfProcedure = {
        type: 'custom',
        message: procedureDateBeforeDobError,
      };
    }

    // Date of Discharge validation: required and not in future when patient
    // status is Hospital Inpatient or Hospital Outpatient. For Non-Hospital
    // Outpatient, the field is optional and ignored if empty.
    if (
      data.patientStatus === PatientStatusAtBiopsy.HospitalInpatient ||
      data.patientStatus === PatientStatusAtBiopsy.HospitalOutpatient
    ) {
      if (!data.dateOfDischarge || !data.dateOfDischarge.trim()) {
        customErrors.dateOfDischarge = {
          type: 'custom',
          message:
            'Date of Discharge is required for hospital inpatients/outpatients',
        };
      } else {
        try {
          const parsed = parse(data.dateOfDischarge, 'yyyy-MM-dd', new Date());
          if (!isValid(parsed)) {
            customErrors.dateOfDischarge = {
              type: 'custom',
              message: 'Date of Discharge must be a valid date',
            };
          } else if (parsed.getFullYear() < MIN_DISCHARGE_DATE.getFullYear()) {
            customErrors.dateOfDischarge = {
              type: 'custom',
              message: `Date of Discharge must be a valid date (year ${MIN_DISCHARGE_DATE.getFullYear()} or later)`,
            };
          } else {
            const today = startOfDay(new Date());
            if (isAfter(parsed, today)) {
              customErrors.dateOfDischarge = {
                type: 'custom',
                message: 'Date of Discharge cannot be in the future',
              };
            }
          }
        } catch {
          customErrors.dateOfDischarge = {
            type: 'custom',
            message: 'Date of Discharge must be a valid date',
          };
        }
      }
    }

    // Custom ICD-10 code validation: required when "Other" is selected
    if (data.isOtherDiagnosisCodeSelected) {
      if (!data.customPrimaryDiagnosisCode?.trim()) {
        customErrors.customPrimaryDiagnosisCode = {
          type: 'custom',
          message: 'Custom ICD-10 Code is required when "Other" is selected',
        };
      }
    }

    // At least one ICD-10 diagnosis code must be selected (C61 or Other)
    if (
      data.primaryDiagnosisCodes.length === 0 &&
      !data.isOtherDiagnosisCodeSelected
    ) {
      customErrors.primaryDiagnosisCodes = {
        type: 'custom',
        message: 'At least one ICD-10 Primary Diagnosis Code is required',
      };
    }

    const result = {
      values: data,
      errors: customErrors,
      warnings: warnings,
    } as ResolverResult<LdtProstatePostRPFormData> & {
      warnings: Partial<Record<keyof LdtProstatePostRPFormData, string>>;
    };

    // Store warnings in ref if provided (from the returned warnings)
    if (warningsRef) {
      warningsRef.current = result.warnings;
    }

    return result;
  };

// Helper function to compute PSA warning
const computePsaWarning = (psaValue?: number): string => {
  if (!psaValue || psaValue <= 5) return '';
  return 'PSA exceeds 5.00 ng/mL. Please verify the provided PSA is the most recent PSA after prostatectomy.';
};

// Helper function to create default form values
export const createProstatePostRPDefaultFormValues = (
  initialOrder: LdtOrderDetailFieldsFragment,
): LdtProstatePostRPFormData => {
  // Check if there's an existing TRF file in the order
  let testRequisitionForm: File | null = null;
  let pathologyReport: File | null = null;
  let faceSheet: File | null = null;
  if (initialOrder.uploadedFiles?.edges) {
    const trfFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.TestRequisitionForm &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (trfFile?.node) {
      // Create a placeholder File object to represent the existing upload
      // This satisfies the schema validation which expects a File instance
      testRequisitionForm = new File([], trfFile.node.filename);
    }
    const pathologyReportFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.PathologyReport &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (pathologyReportFile?.node) {
      pathologyReport = new File([], pathologyReportFile.node.filename);
    }
    const faceSheetFile = initialOrder.uploadedFiles.edges.find(
      (edge) =>
        edge?.node?.category === UploadedFileCategory.FaceSheet &&
        edge.node.status === UploadedFileStatus.Complete,
    );
    if (faceSheetFile?.node) {
      faceSheet = new File([], faceSheetFile.node.filename);
    }
  }

  const primaryDiagnosisCodes = initialOrder.primaryDiagnosisCodes?.filter(
    (code): code is PrimaryDiagnosisCode => !!code,
  ) ?? [PrimaryDiagnosisCode.C61];

  return {
    orderingProviderId: initialOrder.orderingProvider?.id ?? '',
    firstName: initialOrder.patient?.firstNamePhi ?? '',
    lastName: initialOrder.patient?.lastNamePhi ?? '',
    dateOfBirth: initialOrder.patient?.dateOfBirthPhi ?? '',
    ethnicity: (initialOrder.patient?.ethnicity ?? '') as Ethnicity,
    primaryGleasonScore: (initialOrder.primaryGleasonScore?.toString() ??
      undefined) as '3' | '4' | '5',
    secondaryGleasonScore: (initialOrder.secondaryGleasonScore?.toString() ??
      undefined) as '3' | '4' | '5',
    baselinePsa: initialOrder.psa ?? null,
    tStage: (initialOrder.tStage ?? '') as TStage,
    surgicalMargin: (initialOrder.surgicalMargin ?? '') as SurgicalMargin,
    patientStatus: (initialOrder.patientStatusAtBiopsy ??
      '') as PatientStatusAtBiopsy,
    dateOfDischarge: initialOrder.patientDischargeDate ?? '',
    primaryDiagnosisCodes,
    customPrimaryDiagnosisCode: initialOrder.customPrimaryDiagnosisCode ?? '',
    isOtherDiagnosisCodeSelected: !!initialOrder.customPrimaryDiagnosisCode,
    dateOfProcedure: initialOrder.labProcedureDatePhi ?? '',
    patientLabAccession: initialOrder.patientLabAccession ?? '',
    slideSamplePart: initialOrder.slideSamplePart ?? '',
    testRequisitionForm,
    pathologyReport,
    faceSheet,
  };
};

export const LdtProstatePostRPOrderForm: FC<{
  orderRepository: OrderRepository;
  initialOrder: LdtOrderDetailFieldsFragment;
  onClose: () => void;
  workflowType: WorkflowType;
}> = ({ orderRepository, initialOrder, onClose, workflowType }) => {
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const isOrderingPhysicianUser = useIsOrderingPhysician();
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const ldtOrderRepository = orderRepository as LdtOrderRepository;

  const orderingPathway = initialOrder.orderingPathway;

  // Detect breakpoints for FileUpload divider logic
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg')); // 4 columns
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm')); // 2 columns

  // Ref for the custom ICD-10 code field to enable auto-scroll when "Other" is selected
  const customPrimaryDiagnosisCodeFieldRef = useRef<HTMLInputElement>(null);
  // Ref for the date of discharge field to enable auto-scroll when Hospital In/Outpatient is selected
  const dateOfDischargeFieldRef = useRef<HTMLInputElement>(null);
  // Ref to store warnings computed in the resolver
  const warningsRef = useRef<
    Partial<Record<keyof LdtProstatePostRPFormData, string>>
  >({});

  // Check if we're editing a QC Pending order (for CS/Sales users)
  const isEditingQCPendingOrder =
    isCSOrSalesUser &&
    initialOrder.status === OrderStatus.QualityControlPending;
  const softClinicalSubmit = allowSoftClinicalSubmit(
    isCSOrSalesUser,
    isEditingQCPendingOrder,
    workflowType,
  );

  const allowTStageNotProvided = canUseClinicalNotProvided(
    isCSOrSalesUser,
    useEffectiveOrganization().features.allowTStageNotProvided,
    workflowType,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
    watch,
  } = useForm<LdtProstatePostRPFormData>({
    resolver: createProstatePostRPCustomResolver(
      isCSOrSalesUser,
      orderingPathway,
      isEditingQCPendingOrder,
      warningsRef,
      allowTStageNotProvided,
      workflowType,
    ),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: createProstatePostRPDefaultFormValues(initialOrder),
  });

  // Validate form immediately when CS/Sales user opens QC Pending order for editing
  useEffect(() => {
    if (isEditingQCPendingOrder) {
      // Trigger validation for all fields when form opens
      trigger();
    }
  }, [isEditingQCPendingOrder, trigger]);

  const {
    selectedOrderingProvider,
    orderingProviderSectionProps,
    orderingProviderDialogProps,
  } = useOrderingProviderFormSection({
    ldtOrderRepository,
    isOrderingPhysicianUser,
    initialOrderingProvider: initialOrder.orderingProvider ?? null,
    setOrderingProviderId: (id, options) =>
      setValue('orderingProviderId', id, options),
    triggerOrderingProviderValidation: () => trigger('orderingProviderId'),
  });

  const patientStatus = watch('patientStatus');
  const isOtherDiagnosisCodeSelected = watch('isOtherDiagnosisCodeSelected');

  // Track previous values to only scroll on transitions (not initial load)
  const previousPatientStatusRef = useRef<PatientStatusAtBiopsy | undefined>(
    patientStatus as PatientStatusAtBiopsy | undefined,
  );
  const prevIsOtherPrimaryDiagnosisCodeSelectedRef = useRef<boolean>(
    isOtherDiagnosisCodeSelected,
  );

  // When patient status switches to Non-Hospital Outpatient or Not Provided, clear the
  // Date of Discharge field and avoid validating it.
  useEffect(() => {
    if (
      patientStatus === PatientStatusAtBiopsy.NonHospitalOutpatient ||
      patientStatus === PatientStatusAtBiopsy.NotProvided
    ) {
      setValue('dateOfDischarge', '', { shouldValidate: false });
    }
  }, [patientStatus, setValue]);

  // Auto-scroll to Date of Discharge field when Hospital In/Outpatient is selected
  useEffect(() => {
    const isHospitalStatus =
      patientStatus === PatientStatusAtBiopsy.HospitalInpatient ||
      patientStatus === PatientStatusAtBiopsy.HospitalOutpatient;
    const wasHospitalStatus =
      previousPatientStatusRef.current ===
        PatientStatusAtBiopsy.HospitalInpatient ||
      previousPatientStatusRef.current ===
        PatientStatusAtBiopsy.HospitalOutpatient;

    // Only scroll if transitioning from non-hospital to hospital status
    if (
      !wasHospitalStatus &&
      isHospitalStatus &&
      dateOfDischargeFieldRef.current
    ) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double RAF ensures the element is fully rendered
          dateOfDischargeFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          // Focus after a short delay to allow scroll to start
          requestAnimationFrame(() => {
            dateOfDischargeFieldRef.current?.focus();
          });
        });
      });
    }

    // Update ref for next comparison
    previousPatientStatusRef.current = patientStatus as
      | PatientStatusAtBiopsy
      | undefined;
  }, [patientStatus]);

  // Auto-scroll to Custom ICD-10 Code field when "Other" is checked
  useEffect(() => {
    const wasOtherPrimaryDiagnosisCodeSelected =
      prevIsOtherPrimaryDiagnosisCodeSelectedRef.current;

    // Only scroll if transitioning from unchecked to checked
    if (
      !wasOtherPrimaryDiagnosisCodeSelected &&
      isOtherDiagnosisCodeSelected &&
      customPrimaryDiagnosisCodeFieldRef.current
    ) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double RAF ensures the element is fully rendered
          customPrimaryDiagnosisCodeFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          // Focus after a short delay to allow scroll to start
          requestAnimationFrame(() => {
            customPrimaryDiagnosisCodeFieldRef.current?.focus();
          });
        });
      });
    }

    prevIsOtherPrimaryDiagnosisCodeSelectedRef.current =
      isOtherDiagnosisCodeSelected;
  }, [isOtherDiagnosisCodeSelected]);

  const debouncedPsaValidation = useDebounce(() => {
    trigger('baselinePsa');
  }, 300);

  // Use the custom hook for file upload management
  const {
    getFilesForCategory,
    handleFileSelected,
    handleFileRemoved,
    isFileDeleting,
  } = useLdtFileUploads({
    order: initialOrder,
    orderRepository: ldtOrderRepository,
    onFileCategoryUpdate: useCallback(
      (
        category: UploadedFileCategory,
        file?: LdtUploadedFileFieldsFragment,
        fileObject?: File,
      ) => {
        // Update form value for TRF if a TRF file was added or removed
        if (category === UploadedFileCategory.TestRequisitionForm) {
          if (file) {
            // Use the fileObject from the callback, or create a placeholder File
            const fileObj = fileObject || new File([], file.filename);
            setValue('testRequisitionForm', fileObj, {
              shouldValidate: true,
            });
          } else {
            setValue('testRequisitionForm', null, {
              shouldValidate: true,
            });
          }
        }
        // Update form value for Pathology Report if a file was added or removed
        if (category === UploadedFileCategory.PathologyReport) {
          if (file) {
            const fileObj = fileObject || new File([], file.filename);
            setValue('pathologyReport', fileObj, {
              shouldValidate: true,
            });
          } else {
            setValue('pathologyReport', null, {
              shouldValidate: true,
            });
          }
        }
        // Update form value for Face Sheet if a file was added or removed
        if (category === UploadedFileCategory.FaceSheet) {
          if (file) {
            const fileObj = fileObject || new File([], file.filename);
            setValue('faceSheet', fileObj, {
              shouldValidate: true,
            });
          } else {
            setValue('faceSheet', null, {
              shouldValidate: true,
            });
          }
        }
      },
      [setValue],
    ),
    onError: notifyError,
  });

  // Helper function to determine if an item should show divider
  // based on its position and current breakpoint
  const shouldShowDivider = useCallback(
    (index: number, totalItems: number) => {
      if (!isSmUp) {
        // xs: single column, no dividers
        return false;
      }

      if (isLgUp) {
        // lg: 4 columns
        // Show divider if not in last column of any row
        const columnPosition = index % 4; // 0, 1, 2, 3
        return columnPosition !== 3 && index !== totalItems - 1;
      } else {
        // sm: 2 columns
        // Show divider if not in last column of any row
        const columnPosition = index % 2; // 0, 1
        return columnPosition !== 1 && index !== totalItems - 1;
      }
    },
    [isLgUp, isSmUp],
  );

  // Memoize files for each category - recalculates when uploads change to show progress updates
  const trfFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.TestRequisitionForm),
    [getFilesForCategory],
  );
  const faceSheetFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.FaceSheet),
    [getFilesForCategory],
  );
  const pathologyReportFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.PathologyReport),
    [getFilesForCategory],
  );
  const insuranceFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Insurance),
    [getFilesForCategory],
  );
  const consultReportFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.ConsultReport),
    [getFilesForCategory],
  );
  const attestationFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Attestation),
    [getFilesForCategory],
  );
  const otherFiles = useMemo(
    () => getFilesForCategory(UploadedFileCategory.Other),
    [getFilesForCategory],
  );

  // Create file upload configurations based on user role and ordering pathway
  // This pattern allows for role-based field visibility and requirements
  const fileUploadConfigs = useMemo(() => {
    // TRF is required for CS/Sales users unless pathway is StickerTrfNonReflexive
    // TRF section is completely hidden for StickerTrfNonReflexive pathway
    const isTrfRequired =
      isCSOrSalesUser &&
      orderingPathway !== OrderingPathway.StickerTrfNonReflexive;

    // Pathology Report and Demographic/Face Sheet are required for CS/Sales users
    // when editing a QC Pending order
    const isEditingQCPendingOrder =
      isCSOrSalesUser &&
      initialOrder.status === OrderStatus.QualityControlPending;

    const configs: Array<{
      label: string;
      category: UploadedFileCategory;
      required?: boolean;
      helperText?: string;
      accept?: string;
      allowMultiple?: boolean;
      files: LdtUploadedFileFieldsFragment[];
      showDivider: (index: number, total: number) => boolean;
      visible: boolean; // CS-only visibility control
    }> = [
      {
        label: 'Test Requisition Form',
        category: UploadedFileCategory.TestRequisitionForm,
        required: isTrfRequired,
        helperText: 'only one PDF file is allowed',
        accept: '.pdf',
        allowMultiple: false,
        files: trfFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isTrfRequired, // Hidden for StickerTrfNonReflexive pathway
      },
      {
        label: 'Demographic / Face Sheet',
        category: UploadedFileCategory.FaceSheet,
        required: isEditingQCPendingOrder,
        files: faceSheetFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Pathology Report',
        category: UploadedFileCategory.PathologyReport,
        required: isEditingQCPendingOrder,
        files: pathologyReportFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Insurance Card(s)',
        category: UploadedFileCategory.Insurance,
        files: insuranceFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: true,
      },
      {
        label: 'Attestation',
        category: UploadedFileCategory.Attestation,
        files: attestationFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
      {
        label: 'Consult Report',
        category: UploadedFileCategory.ConsultReport,
        files: consultReportFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
      {
        label: 'Other(s)',
        category: UploadedFileCategory.Other,
        files: otherFiles,
        showDivider: (index, total) => shouldShowDivider(index, total),
        visible: isCSOrSalesUser,
      },
    ];

    // Filter out invisible configs and return only visible ones
    return configs.filter((config) => config.visible);
  }, [
    isCSOrSalesUser,
    initialOrder.status,
    orderingPathway,
    trfFiles,
    faceSheetFiles,
    pathologyReportFiles,
    insuranceFiles,
    consultReportFiles,
    attestationFiles,
    otherFiles,
    shouldShowDivider,
  ]);

  // Convert form data to Order object
  const getOrderFromFormData = useCallback((): LdtOrderDetailFieldsFragment => {
    const formData = getValues();

    // Exclude uploadedFiles from initialOrder spread since it's managed separately
    // via cancelUpload/completeUpload and should be preserved from the persisted order
    const { uploadedFiles: _uploadedFiles, ...orderWithoutFiles } =
      initialOrder;

    return {
      ...orderWithoutFiles,
      orderingProvider: {
        ...initialOrder.orderingProvider,
        id: formData.orderingProviderId || null,
      },
      patient: {
        ...initialOrder.patient,
        firstNamePhi: formData.firstName.trim() || null,
        lastNamePhi: formData.lastName.trim() || null,
        dateOfBirthPhi: formData.dateOfBirth || null,
        ethnicity: safeEmptyToUndefined(formData.ethnicity) as
          | Ethnicity
          | undefined,
      },
      primaryGleasonScore: safeParseInt(formData.primaryGleasonScore),
      secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore),
      psa: formData.baselinePsa,
      tStage: safeEmptyToUndefined(formData.tStage) as TStage | undefined,
      surgicalMargin: safeEmptyToUndefined(formData.surgicalMargin) as
        | SurgicalMargin
        | undefined,
      patientStatusAtBiopsy: safeEmptyToUndefined(formData.patientStatus) as
        | PatientStatusAtBiopsy
        | undefined,
      primaryDiagnosisCodes: formData.primaryDiagnosisCodes,
      customPrimaryDiagnosisCode:
        formData.customPrimaryDiagnosisCode?.trim() || null,
      patientDischargeDate: formData.dateOfDischarge || null,
      labProcedureDatePhi: formData.dateOfProcedure?.trim() || null,
      patientLabAccession: formData.patientLabAccession?.trim() || null,
      slideSamplePart: null,
      qcRevisionReason: initialOrder.qcRevisionReason || null,
      specimenType: SpecimenType.ProstatePostRp,
      orderingPathway: initialOrder.orderingPathway,
      status: initialOrder.status,
    } as LdtOrderDetailFieldsFragment;
  }, [getValues, initialOrder]);

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = useNewOrderSignal(initialOrder.id);
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  // Trigger validation right when the form opens if this is an existing order.
  useEffect(() => {
    if (!isNewOrder) {
      trigger();
    }
  }, [isNewOrder, trigger]);

  const handleSaveOrder = async (
    qcRevisionReason?: string,
    revertStatus?: OrderStatus,
  ) => {
    const orderData = getOrderFromFormData();

    // When saving a QC Pending LDT order that has an AI report, set the revert status.
    if (revertStatus) {
      orderData.status = revertStatus;
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE_REJECTED_ORDER,
        location,
        orderData,
      );
    } else if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      // Include qcRevisionReason if provided (convert empty string to null)
      if (qcRevisionReason) {
        orderData.qcRevisionReason = qcRevisionReason.trim() || null;
      }

      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();

    // Submit draft orders; otherwise, update the order.
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.UPDATE;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    if (isDraftOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const isLdtNonTempusQcPending =
    isEditingQCPendingOrder &&
    [
      WorkflowType.CLINIC,
      WorkflowType.ANCILLARY_LAB,
      WorkflowType.DIRECT_UPLOAD_LAB,
    ].includes(workflowType);
  const hasAiReport = initialOrder.hasAiReport ?? false;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '2000px',
        maxHeight: '1500px',
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        px: 2,
      }}
    >
      {/* Header */}
      <LdtOrderFormHeader
        title={initialOrder.crossOrgId ? 'Edit Order' : 'Create Order'}
        specimenType={SpecimenType.ProstatePostRp}
        onClose={onClose}
        crossOrgId={initialOrder.crossOrgId}
      />
      <Typography
        variant='caption'
        sx={{
          color: 'text.secondary',
        }}
      >
        *Indicates a required field
      </Typography>
      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          mt: 2,
        }}
      >
        {/* Ordering Provider Section */}
        <OrderingProviderFormSection
          control={control}
          errors={errors}
          trigger={trigger}
          ldtOrderRepository={ldtOrderRepository}
          selectedOrderingProvider={selectedOrderingProvider}
          {...orderingProviderSectionProps}
        />

        {/* Upload Document Section */}
        <FormSectionCard
          header={
            <SectionHeader
              title='Upload Document'
              subtitle='pdf, png, jpeg types are supported'
            />
          }
          sx={{ borderBottom: 'none' }}
        >
          <Grid container spacing={2}>
            {fileUploadConfigs.map((config, index) => (
              <Grid key={config.category} size={{ xs: 12, sm: 6, lg: 3 }}>
                <FileUpload
                  label={config.label}
                  category={config.category}
                  required={config.required}
                  helperText={config.helperText}
                  isFileDeleting={isFileDeleting}
                  validationText={
                    config.category === UploadedFileCategory.TestRequisitionForm
                      ? // Don't show validation text if TRF is uploading, queued, or completed
                        trfFiles.length > 0
                        ? undefined
                        : errors.testRequisitionForm?.message
                      : config.category === UploadedFileCategory.PathologyReport
                        ? // Don't show validation text if Pathology Report has files
                          pathologyReportFiles.length > 0
                          ? undefined
                          : errors.pathologyReport?.message
                        : config.category === UploadedFileCategory.FaceSheet
                          ? // Don't show validation text if Face Sheet has files
                            faceSheetFiles.length > 0
                            ? undefined
                            : errors.faceSheet?.message
                          : undefined
                  }
                  onFileSelected={handleFileSelected}
                  onFileRemoved={handleFileRemoved}
                  accept={config.accept}
                  allowMultiple={config.allowMultiple}
                  showDivider={config.showDivider(
                    index,
                    fileUploadConfigs.length,
                  )}
                  files={config.files}
                />
              </Grid>
            ))}
          </Grid>
        </FormSectionCard>

        {/* Patient Information Section */}
        <PatientInformationFormSection control={control} errors={errors} />

        {/* Clinical Information Section */}
        <FormSectionCard
          header={<SectionHeader title='Clinical Information' />}
          sx={{ borderBottom: 'none' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Gleason Scores Row */}
            {isCSOrSalesUser && (
              <GleasonScoreFields
                control={control}
                errors={errors}
                trigger={trigger}
              />
            )}

            {/* PSA and T-Stage Row */}
            <ResponsiveFormRow>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Controller
                  name='baselinePsa'
                  control={control}
                  render={({ field }) => {
                    const psaValue = field.value;
                    const hasError = !!errors.baselinePsa;
                    // Get warning from resolver (computed in resolver)
                    const warningText = warningsRef.current.baselinePsa;

                    return (
                      <DecimalInput
                        required={!softClinicalSubmit}
                        fullWidth
                        label='Most Recent PSA after prostatectomy (ng/mL)'
                        value={psaValue}
                        decimalPlaces={2}
                        min={FORM_CONSTANTS.PSA_MIN}
                        max={FORM_CONSTANTS.MAX_PSA}
                        error={hasError}
                        validationText={errors.baselinePsa?.message}
                        warningText={warningText}
                        onBlur={field.onBlur}
                        onChange={(value) => {
                          field.onChange(value);
                          // Debounced PSA validation - triggers after 300ms of no typing
                          debouncedPsaValidation();
                        }}
                      />
                    );
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              />
            </ResponsiveFormRow>
            <LdtPostRpTStageAndSurgicalMarginFields
              control={control}
              errors={errors}
              trigger={trigger}
              allowTStageNotProvided={allowTStageNotProvided}
            />
          </Box>
        </FormSectionCard>

        {/* Specimen Information Section - CS/Sales only */}
        {isCSOrSalesUser && (
          <SpecimenInformationFormSection
            specimenType={SpecimenType.ProstatePostRp}
            control={control}
            errors={errors}
            trigger={trigger}
            required={isEditingQCPendingOrder}
            warningsRef={warningsRef}
          />
        )}

        {/* Billing Information Section */}
        <BillingInformationFormSection
          specimenType={SpecimenType.ProstatePostRp}
          control={control}
          errors={errors}
          trigger={trigger}
          patientStatus={patientStatus}
          dateOfDischargeFieldRef={dateOfDischargeFieldRef}
          customPrimaryDiagnosisCodeFieldRef={
            customPrimaryDiagnosisCodeFieldRef
          }
          workflowType={WorkflowType.CLINIC}
          clearCustomPrimaryDiagnosisCode={() => {
            setValue('customPrimaryDiagnosisCode', '', {
              shouldValidate: false,
            });
            setValue('isOtherDiagnosisCodeSelected', false, {
              shouldValidate: false,
            });
          }}
        />
      </Box>
      {/* Fixed FormActions at the bottom */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 2,
        }}
      >
        <LdtFormActions
          showSubmitButton={
            [WorkflowType.CLINIC, WorkflowType.DIRECT_UPLOAD_LAB].includes(
              workflowType,
            ) && isDraftOrder
          }
          showSaveButton={isEditingQCPendingOrder}
          showDraftActionButtons={
            (isDraftOrder || isNewOrder) &&
            [WorkflowType.CLINIC, WorkflowType.DIRECT_UPLOAD_LAB].includes(
              workflowType,
            )
          }
          isSubmitting={fetcher.state !== 'idle'}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
          showRevisionReasonDialog={isLdtNonTempusQcPending && !hasAiReport}
          showSaveWithReportDialog={isLdtNonTempusQcPending && hasAiReport}
          onConfirmSave={(qcRevisionReason) =>
            handleSaveOrder(qcRevisionReason)
          }
          onConfirmSaveWithReport={(revertStatus: OrderStatus) =>
            handleSaveOrder(undefined, revertStatus)
          }
          onFileSelected={handleFileSelected}
          onFileRemoved={handleFileRemoved}
          isFileDeleting={isFileDeleting}
          otherFiles={otherFiles}
          existingQcRevisionReason={initialOrder.qcRevisionReason}
        />
      </Box>
      {/* Ordering Provider Form Dialog */}
      {orderingProviderDialogProps.open && (
        <OrderingProviderForm
          open
          orderRepository={ldtOrderRepository}
          onClose={orderingProviderDialogProps.onClose}
          initialProvider={orderingProviderDialogProps.initialProvider}
          onSuccess={orderingProviderDialogProps.onSuccess}
          onDelete={orderingProviderDialogProps.onDelete}
        />
      )}

      <PreventLeaveDuringUploadDialog orderId={initialOrder.id} />
    </Box>
  );
};
